import React from 'react';
import { ArrowLeftRight, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { formatRial } from '@/lib/formatMoney';
import {
  bsiInternetBankApi,
  type BsiIbAccountOption,
  type BsiIbSummaryAccount,
  type BsiIbSummaryTotals,
  type BsiIbSyncSkipped,
  type BsiIbSyncUpdated,
} from '@/lib/banks/bsi/api';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
  onOpenLastThirty: (accountNumber: string) => void;
  onOpenPeriod: (accountNumber: string) => void;
};

const BsiAccountsTab: React.FC<Props> = ({
  sessionValid,
  onMessage,
  onSessionExpired,
  onOpenLastThirty,
  onOpenPeriod,
}) => {
  const [summaryAccounts, setSummaryAccounts] = React.useState<BsiIbSummaryAccount[]>([]);
  const [summaryTotals, setSummaryTotals] = React.useState<BsiIbSummaryTotals | null>(null);
  const [summaryCurrencies, setSummaryCurrencies] = React.useState<BsiIbAccountOption[]>([]);
  const [summaryCurrency, setSummaryCurrency] = React.useState('00');
  const [loadingSummary, setLoadingSummary] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [syncUpdated, setSyncUpdated] = React.useState<BsiIbSyncUpdated[]>([]);
  const [syncSkipped, setSyncSkipped] = React.useState<BsiIbSyncSkipped[]>([]);
  const [syncUnmatched, setSyncUnmatched] = React.useState<string[]>([]);

  const loadSummary = React.useCallback(
    async (currencyCode?: string) => {
      setLoadingSummary(true);
      onMessage(null);
      try {
        const res = await bsiInternetBankApi.accountSummary(currencyCode || summaryCurrency || '00');
        setSummaryAccounts(res.data.accounts || []);
        setSummaryTotals(res.data.totals || null);
        setSummaryCurrencies(res.data.currencies || []);
        if (res.data.currency_code) setSummaryCurrency(res.data.currency_code);
        onMessage({ type: 'success', text: res.message || 'خلاصه حساب‌ها دریافت شد.' });
      } catch (e) {
        const text = e instanceof Error ? e.message : 'خطا در دریافت خلاصه حساب';
        onMessage({ type: 'error', text });
        if (isSessionExpiredMessage(text)) onSessionExpired();
      } finally {
        setLoadingSummary(false);
      }
    },
    [summaryCurrency, onMessage, onSessionExpired],
  );

  const booted = React.useRef(false);
  React.useEffect(() => {
    if (!sessionValid || summaryAccounts.length > 0 || loadingSummary || booted.current) return;
    booted.current = true;
    void loadSummary('00');
  }, [sessionValid, summaryAccounts.length, loadingSummary, loadSummary]);

  const handleSyncBalances = async () => {
    setSyncing(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.syncBalances(summaryCurrency || '00');
      setSyncUpdated(res.data.updated || []);
      setSyncSkipped(res.data.skipped || []);
      setSyncUnmatched(res.data.unmatched || []);
      if (res.data.totals) setSummaryTotals(res.data.totals);
      onMessage({ type: 'success', text: res.message || 'همگام‌سازی انجام شد.' });
      await loadSummary(summaryCurrency);
    } catch (err) {
      onMessage({ type: 'error', text: err instanceof Error ? err.message : 'همگام‌سازی ناموفق بود.' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="text-base">حساب‌ها و مانده</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sessionValid ? (
            <p className="text-sm text-amber-700">برای مشاهده مانده، ابتدا از تب «ورود» وارد اینترنت‌بانک شوید.</p>
          ) : (
            <>
              <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                خلاصه همه حساب‌ها از پنل بانک (موجودی جاری، مؤثر و قابل‌برداشت).
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px] space-y-2">
                  <Label>نوع ارز</Label>
                  <SearchableSelect
                    value={summaryCurrency}
                    onChange={setSummaryCurrency}
                    options={
                      summaryCurrencies.length
                        ? summaryCurrencies.map((c) => ({ value: c.value, label: c.label || c.value }))
                        : [{ value: '00', label: 'ریال ایران' }]
                    }
                    placeholder="ارز"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => void loadSummary(summaryCurrency)}
                  disabled={loadingSummary || syncing}
                >
                  {loadingSummary ? (
                    <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="ml-1 h-4 w-4" />
                  )}
                  بارگذاری مانده
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleSyncBalances()}
                  disabled={syncing || loadingSummary}
                >
                  {syncing ? (
                    <Loader2 className="ml-1 h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowLeftRight className="ml-1 h-4 w-4" />
                  )}
                  همگام‌سازی با حساب‌های محلی
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                همگام‌سازی با تطبیق شماره حساب، موجودی جاری را در «حساب‌های بانکی» سیستم به‌روز می‌کند.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {summaryAccounts.length > 0 && (
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">نتیجه — {summaryAccounts.length} حساب</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {summaryTotals && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm">
                    <div className="text-xs text-slate-500">{summaryTotals.label || 'جمع'} — موجودی جاری</div>
                    <div className="font-mono font-semibold">{formatRial(summaryTotals.current_balance)}</div>
                  </div>
                  <div className="rounded-md border bg-emerald-50 px-3 py-2 text-sm">
                    <div className="text-xs text-emerald-700">موجودی قابل‌برداشت</div>
                    <div className="font-mono font-semibold text-emerald-800">
                      {formatRial(summaryTotals.available_balance)}
                    </div>
                  </div>
                  <div className="rounded-md border bg-sky-50 px-3 py-2 text-sm">
                    <div className="text-xs text-sky-700">موجودی مؤثر</div>
                    <div className="font-mono font-semibold text-sky-800">
                      {formatRial(summaryTotals.effective_balance)}
                    </div>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto rounded border">
                <table className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-right">
                      <th className="border px-2 py-2">شماره حساب</th>
                      <th className="border px-2 py-2">نوع</th>
                      <th className="border px-2 py-2">شعبه</th>
                      <th className="border px-2 py-2">موجودی جاری</th>
                      <th className="border px-2 py-2">قابل‌برداشت</th>
                      <th className="border px-2 py-2">مؤثر</th>
                      <th className="border px-2 py-2">آخرین گردش</th>
                      <th className="border px-2 py-2">افتتاح</th>
                      <th className="border px-2 py-2">عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryAccounts.map((a) => (
                      <tr key={a.account_number}>
                        <td className="border px-2 py-1.5 font-mono text-xs">{a.account_number}</td>
                        <td className="border px-2 py-1.5 max-w-[180px] truncate" title={a.account_type}>
                          {a.account_type || '—'}
                        </td>
                        <td className="border px-2 py-1.5">{a.branch_code || '—'}</td>
                        <td className="border px-2 py-1.5 font-mono">{formatRial(a.current_balance)}</td>
                        <td className="border px-2 py-1.5 font-mono text-emerald-700">
                          {formatRial(a.available_balance)}
                        </td>
                        <td className="border px-2 py-1.5 font-mono text-sky-700">
                          {formatRial(a.effective_balance)}
                        </td>
                        <td className="border px-2 py-1.5 whitespace-nowrap">{a.last_transaction_date || '—'}</td>
                        <td className="border px-2 py-1.5 whitespace-nowrap">{a.opening_date || '—'}</td>
                        <td className="border px-2 py-1.5">
                          <div className="flex flex-wrap gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenLastThirty(a.account_number)}
                            >
                              سی‌گردش
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => onOpenPeriod(a.account_number)}
                            >
                              دوره‌ای
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {(syncUpdated.length > 0 || syncSkipped.length > 0 || syncUnmatched.length > 0) && (
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">نتیجه همگام‌سازی</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {syncUpdated.length > 0 && (
                <div className="overflow-x-auto rounded border">
                  <table className="w-full min-w-[700px] text-sm">
                    <thead>
                      <tr className="bg-emerald-50 text-right">
                        <th className="border px-2 py-2">حساب محلی</th>
                        <th className="border px-2 py-2">شماره</th>
                        <th className="border px-2 py-2">قبل</th>
                        <th className="border px-2 py-2">بعد</th>
                        <th className="border px-2 py-2">اختلاف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncUpdated.map((u) => (
                        <tr key={u.bank_account_id}>
                          <td className="border px-2 py-1.5">{u.name}</td>
                          <td className="border px-2 py-1.5 font-mono text-xs">{u.account_number}</td>
                          <td className="border px-2 py-1.5 font-mono">{formatRial(u.balance_before)}</td>
                          <td className="border px-2 py-1.5 font-mono">{formatRial(u.balance_after)}</td>
                          <td className="border px-2 py-1.5 font-mono">
                            {u.direction === 'credit' ? '+' : '-'}
                            {formatRial(u.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {syncSkipped.length > 0 && (
                <p className="text-slate-600">{syncSkipped.length} حساب بدون تغییر مانده بودند.</p>
              )}
              {syncUnmatched.length > 0 && (
                <div>
                  <p className="mb-1 text-amber-800">بدون تطبیق در سیستم محلی:</p>
                  <ul className="list-disc pr-5 text-xs font-mono text-amber-900">
                    {syncUnmatched.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default BsiAccountsTab;
